import graphClient from "../../../utils/graphql/graph_client";
import { GetAccomsByPolygon, GetAmenitiesByPolygon } from "../../../utils/graphql/poi-graph";
import { wktPointToLatLng } from "../../../utils/helpers/WKTPointToLatLng";
import {
  boxPolygon,
  corridorBoxes,
  ENDPOINT_ACCOMMODATION_TYPES,
  ENDPOINT_AMENITY_TYPES,
  NearbyPlace,
  placeTypeLabel,
  quarterBox,
  SearchBox,
  searchBoxAround,
} from "./route-endpoints";
import { GooglePlace, importGooglePlace as importPlace } from "../../../utils/services/GoogleMapsPlaces";

export { searchGoogleMaps } from "../../../utils/services/GoogleMapsPlaces";

/**
 * Accommodations and amenities around a route — for picking its Start or End,
 * adding highlights, and finding places to stay. One request per kind per
 * area, remembered for the visit: the same route is usually searched several
 * times in a row.
 */

/** Which accommodation and amenity types to look for. */
export interface PlaceKinds {
  accommodationTypes: string[];
  amenityTypes: string[];
}

const ENDPOINT_KINDS: PlaceKinds = { accommodationTypes: ENDPOINT_ACCOMMODATION_TYPES, amenityTypes: ENDPOINT_AMENITY_TYPES };

/** Area searches running at once, at most — a long route is many areas. */
const CONCURRENT_AREAS = 4;

/**
 * The most places the by-polygon queries return for one area
 * (CONTAINED_IN_POLYGON_LIMIT, server side), the most recently edited first —
 * an area that comes back full has more, so it's searched again in quarters.
 */
export const AREA_RESULT_CAP = 200;

/** How many times an area is quartered at most: down to a sixteenth. */
const MAX_SPLITS = 2;

type Category = NearbyPlace['category'];

const byArea = new Map<string, Promise<NearbyPlace[]>>();

/**
 * Places as the map has them — one without a name goes by its type ("Water",
 * "Bus stop"), as it still has an icon on the map.
 */
function toPlaces(rows: any[] | null | undefined, category: Category): NearbyPlace[] {
  return (rows || []).flatMap(row => {
    const point = row?.lonlat ? wktPointToLatLng(row.lonlat) : null;
    if (!point || !row.type) return [];
    const name = row.name?.trim() || placeTypeLabel(row.type);
    return [{ id: row.id, category, type: row.type, name, coordinates: [point.lng, point.lat] as [number, number] }];
  });
}

async function fetchKind(box: SearchBox, category: Category, types: string[]): Promise<{ places: NearbyPlace[]; full: boolean }> {
  const polygon = boxPolygon(box);
  try {
    if (category === 'Accommodation') {
      const data: any = await graphClient.request(GetAccomsByPolygon, { polygon, types });
      const rows = data?.accommodationsByPolygon ?? [];
      return { places: toPlaces(rows, category), full: rows.length >= AREA_RESULT_CAP };
    }
    const data: any = await graphClient.request(GetAmenitiesByPolygon, { polygon, types });
    const rows = data?.amenitiesByPolygon ?? [];
    return { places: toPlaces(rows, category), full: rows.length >= AREA_RESULT_CAP };
  } catch {
    return { places: [], full: false };
  }
}

/** One kind of place in an area, quartering it while it comes back full. */
function placesOfKind(box: SearchBox, category: Category, types: string[], splits: number = 0): Promise<NearbyPlace[]> {
  // An empty list would mean "every type" to one query and "none" to the other.
  if (types.length === 0) return Promise.resolve([]);
  const key = `${boxPolygon(box)}|${category}|${types.join(',')}`;
  if (!byArea.has(key)) {
    const request = fetchKind(box, category, types).then(async ({ places, full }) => {
      if (!full || splits >= MAX_SPLITS) return places;
      const quarters = await Promise.all(quarterBox(box).map(quarter => placesOfKind(quarter, category, types, splits + 1)));
      return withoutRepeats([...places, ...quarters.flat()]);
    });
    byArea.set(key, request);
    // A failed area isn't remembered, so the next search tries again.
    request.then(places => { if (places.length === 0) byArea.delete(key); });
  }
  return byArea.get(key)!;
}

async function placesInArea(box: SearchBox, kinds: PlaceKinds): Promise<NearbyPlace[]> {
  const [accommodations, amenities] = await Promise.all([
    placesOfKind(box, 'Accommodation', kinds.accommodationTypes),
    placesOfKind(box, 'Amenity', kinds.amenityTypes),
  ]);
  return [...accommodations, ...amenities];
}

/** Each place once — areas overlap. */
function withoutRepeats(places: NearbyPlace[]): NearbyPlace[] {
  const seen = new Set<string>();
  return places.filter(place => {
    const key = `${place.category}-${place.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Places to stay and amenities within reach of a route's line ([lon, lat] points). */
export function nearbyPlaces(routeCoordinates: number[][]): Promise<NearbyPlace[]> {
  const box = searchBoxAround(routeCoordinates);
  if (!box) return Promise.resolve([]);
  return placesInArea(box, ENDPOINT_KINDS);
}

/**
 * Places of the given kinds within `bufferM` of a line, searched a stretch at
 * a time (see corridorAreas) and each listed once.
 */
export async function placesAlongLine(coordinates: number[][], kinds: PlaceKinds, bufferM: number): Promise<NearbyPlace[]> {
  if (coordinates.length === 0) return [];
  const boxes = corridorBoxes(coordinates, bufferM);
  const found: NearbyPlace[][] = [];
  for (let i = 0; i < boxes.length; i += CONCURRENT_AREAS) {
    found.push(...await Promise.all(boxes.slice(i, i + CONCURRENT_AREAS).map(box => placesInArea(box, kinds))));
  }
  return withoutRepeats(found.flat());
}

/**
 * Adds a place picked from Google Maps to our own places (or finds the one we
 * already have) — so what's made from it links to a real POI. Null when it
 * can't be: not signed in, too many added lately, a kind of place we don't
 * keep, or Google Maps unreachable.
 */
export async function importGooglePlace(place: GooglePlace): Promise<NearbyPlace | null> {
  const ours = await importPlace(place.placeId);
  return ours ? { id: ours.id, category: ours.klass, type: ours.type, name: ours.name || place.name, coordinates: ours.coordinates } : null;
}

/**
 * Addresses found for route ends this visit, by rounded coordinates — shared
 * by every route card, since joined days share their end points.
 */
export const rememberedAddresses = new Map<string, string | null>();

/** The key an address is remembered under, for a [lon, lat] point. */
export function addressKey(coord: number[]): string {
  return `${coord[0].toFixed(5)},${coord[1].toFixed(5)}`;
}

/** Forget remembered areas and addresses (tests). */
export function clearNearbyPlaces(): void {
  byArea.clear();
  rememberedAddresses.clear();
}
